import type React from 'react';
import { useCallback, useRef, useState } from 'react';

import { getTouchCenter, getTouchDistance, type MobileZoom } from '../../utils/canvas/canvas';

interface UseTouchZoomProps {
  isMobile: boolean;
  canvasRef: React.RefObject<HTMLCanvasElement | null>;
}

interface UseTouchZoomReturn {
  mobileZoom: MobileZoom;
  setMobileZoom: React.Dispatch<React.SetStateAction<MobileZoom>>;
  lastTouchDistance: number;
  setLastTouchDistance: React.Dispatch<React.SetStateAction<number>>;
  handleTouchStart: (event: TouchEvent) => void;
  handleTouchMove: (event: TouchEvent) => void;
  handleTouchEnd: (event: TouchEvent) => void;
}

const MIN_SCALE = 1;
const MAX_SCALE = 5;

export const useTouchZoom = ({ isMobile, canvasRef }: UseTouchZoomProps): UseTouchZoomReturn => {
  const [mobileZoom, setMobileZoom] = useState<MobileZoom>({
    isZooming: false,
    scale: 1,
    translateX: 0,
    translateY: 0,
  });
  const [lastTouchDistance, setLastTouchDistance] = useState(0);

  const canvasBoundsRef = useRef<DOMRect | null>(null);

  const clampScale = useCallback((scale: number) => {
    return Math.max(MIN_SCALE, Math.min(MAX_SCALE, scale));
  }, []);

  const handleTouchStart = useCallback(
    (event: TouchEvent) => {
      if (!isMobile) return;

      if (event.touches.length === 2) {
        // Two-finger pinch to zoom
        event.preventDefault();
        const distance = getTouchDistance(event.touches);
        setLastTouchDistance(distance);
        setMobileZoom((prev) => ({ ...prev, isZooming: true }));
      }
    },
    [isMobile]
  );

  const handleTouchMove = useCallback(
    (event: TouchEvent) => {
      if (!isMobile) return;

      if (event.touches.length === 2 && mobileZoom.isZooming) {
        // Two-finger pinch to zoom
        event.preventDefault();

        const distance = getTouchDistance(event.touches);
        const center = getTouchCenter(event.touches);

        if (lastTouchDistance > 0) {
          const scaleChange = distance / lastTouchDistance;
          const newScale = clampScale(mobileZoom.scale * scaleChange);

          const canvas = canvasRef.current;
          if (!canvas) {
            return;
          }

          if (!canvasBoundsRef.current) {
            canvasBoundsRef.current = canvas.getBoundingClientRect();
          }

          const rect = canvasBoundsRef.current;
          const pinchX = center.x - rect.left - rect.width / 2;
          const pinchY = center.y - rect.top - rect.height / 2;

          const scaleRatio = newScale / mobileZoom.scale;
          const newTranslateX = mobileZoom.translateX * scaleRatio + pinchX * (1 - scaleRatio);
          const newTranslateY = mobileZoom.translateY * scaleRatio + pinchY * (1 - scaleRatio);

          setMobileZoom((prev) => ({
            ...prev,
            scale: newScale,
            translateX: newTranslateX,
            translateY: newTranslateY,
          }));
        }

        setLastTouchDistance(distance);
      }
    },
    [isMobile, mobileZoom, lastTouchDistance, canvasRef, clampScale]
  );

  const handleTouchEnd = useCallback(
    (event: TouchEvent) => {
      if (!isMobile) return;

      if (event.touches.length < 2) {
        setMobileZoom((prev) => ({ ...prev, isZooming: false }));
        setLastTouchDistance(0);
        canvasBoundsRef.current = null;

        if (mobileZoom.scale < 1.1) {
          setMobileZoom({ isZooming: false, scale: 1, translateX: 0, translateY: 0 });
        }
      }
    },
    [isMobile, mobileZoom.scale]
  );

  return {
    handleTouchEnd,
    handleTouchMove,
    handleTouchStart,
    lastTouchDistance,
    mobileZoom,
    setLastTouchDistance,
    setMobileZoom,
  };
};
